/**
 * /child/access
 * Entry point for child devices (PWA).
 * - If valid session exists → redirect to /child/:id
 * - Otherwise → show instructions + link to /pwa
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useChildDeviceSession } from "@/hooks/useChildDeviceSession";
import { Button } from "@/components/ui/button";
import { PwaUpdatePrompt } from "@/components/PwaUpdatePrompt";

export default function ChildAccess() {
  const navigate = useNavigate();
  const { isChildDevice, childId, isLoading, invalidated } = useChildDeviceSession();

  // If valid session, redirect
  useEffect(() => {
    if (!isLoading && isChildDevice && childId) {
      navigate(`/child/${childId}`, { replace: true });
    }
  }, [isLoading, isChildDevice, childId, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <PwaUpdatePrompt />
        <p className="text-muted-foreground">Sjekker tilgang…</p>
      </div>
    );
  }

  // Session was revoked or expired
  if (invalidated) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 gap-6">
        <PwaUpdatePrompt />
        <div className="text-5xl">🔒</div>
        <h1 className="text-2xl font-bold text-center">Tilgangen er fjernet eller utløpt</h1>
        <p className="text-muted-foreground text-center max-w-xs">
          Be en voksen om å koble enheten på nytt.
        </p>
        <Button onClick={() => navigate("/pwa")} className="w-full max-w-xs rounded-xl">
          Koble enheten på nytt
        </Button>
      </div>
    );
  }

  // No session — show instructions
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 gap-6">
      <PwaUpdatePrompt />
      <div className="text-5xl">🔗</div>
      <h1 className="text-2xl font-bold text-center">Koble enheten</h1>
      <p className="text-muted-foreground text-center max-w-xs">
        Trykk knappen under for å få en koblingskode. Vis koden til en voksen.
      </p>
      <Button onClick={() => navigate("/pwa")} className="w-full max-w-xs rounded-xl">
        Start kobling
      </Button>
    </div>
  );
}
